// The picture of a root (public/images/dhatu/): a framed folk-art illustration of the action, so a learner sees what
// the root means. Renders nothing for a root without a picture (अस). Decorative when `decorative` (the tile already
// names the root), otherwise its alt text is the root, its meaning and what the picture shows.

import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { dhatuPicture } from '@/prakrit/lib/content/dhatu-images'

interface DhatuPictureProps {
    root: string
    /** The root's meaning, for the alt text: "हस (हँसना): A girl laughing …". */
    meaning?: string
    /** The image widths the browser should pick from, as for next/image. */
    sizes: string
    decorative?: boolean
    priority?: boolean
    /** Extra classes for the frame (size, radius, ring). */
    className?: string
    imageClassName?: string
    /** What to show instead for a root without a picture (by default nothing). */
    fallback?: ReactNode
}

export const hasDhatuPicture = (root: string): boolean => dhatuPicture(root) !== null

export const DhatuPicture: FC<DhatuPictureProps> = ({
    root,
    meaning,
    sizes,
    decorative = false,
    priority = false,
    className,
    imageClassName,
    fallback = null
}): ReactNode => {
    const picture = dhatuPicture(root)

    if (!picture) return fallback

    const alt = decorative ? '' : `${root}${meaning ? ` (${meaning})` : ''}: ${picture.scene}`

    return (
        <span className={cn('relative block aspect-square overflow-hidden rounded-2xl bg-surface-muted ring-1 ring-border', className)}>
            <Image
                src={picture.src}
                alt={alt}
                width={1024}
                height={1024}
                sizes={sizes}
                priority={priority}
                className={cn('size-full object-cover', imageClassName)}
            />
        </span>
    )
}
